import { useCallback, useEffect, useRef, useState } from "react";

import { getAccountDetail } from "@/api/accounts";
import type { AccountDetailVO } from "@/api/accounts/type";

/**
 * 拉取账号详情数据
 * @param id 业务账号 ID
 * @param enabled 是否发起请求，弹窗关闭或未选中行时传 false
 */
export const useAccountDetailData = (id?: string, enabled = true) => {
  const [accountDetail, setAccountDetail] = useState<AccountDetailVO | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  /** 按 id 拉取账号详情 */
  const loadAccountDetail = useCallback(async (userId: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await getAccountDetail(userId);
      if (requestId !== requestIdRef.current) return;
      const detail = res.data;
      setAccountDetail(detail ?? null);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setAccountDetail(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled || !id) {
      return;
    }
    void loadAccountDetail(id);
  }, [enabled, id, loadAccountDetail]);

  /** 关闭或未启用时清空，避免下次打开闪旧数据 */
  useEffect(() => {
    if (enabled) return;
    requestIdRef.current += 1;
    setAccountDetail(null);
    setLoading(false);
  }, [enabled]);

  return {
    accountDetail,
    loading,
    reload: loadAccountDetail,
  };
};
